import { useEffect, useMemo, useState } from 'react';
import { usePageTitle } from '@/hooks/use-page-title';
import { useCapabilityList } from '@/hooks/use-entity-list';
import { useListFilters } from '@/hooks/use-list-filters';
import { useHeaderAction } from '@/lib/header-action-context';
import { useParams, Link, useLocation } from 'react-router';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    CAPABILITY_TYPES,
    CAPABILITY_TYPE_LABELS,
    capabilityDetailPath,
    capabilityDisplayTitle,
} from '@/types';
import {
    capabilityTitleIconBackground,
    getCapabilityColors,
    getStatusStyle,
} from '@/lib/capability-colors';
import { Clock, Tag } from 'lucide-react';
import type { Capability } from '@/types';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { ListPaginationFooter } from '@/components/capability-list/ListPaginationFooter';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

const SORT_OPTIONS = [
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
    { key: 'modified', label: 'Modified' },
    { key: 'created', label: 'Created' },
];

function CapabilityCardInner({
    item,
    typeFilterActive,
    showDetail,
    descriptionLineClamp,
    largeText,
}: {
    item: Capability;
    typeFilterActive: boolean;
    showDetail: boolean;
    descriptionLineClamp: boolean;
    /** ~10% larger typography (popup card) */
    largeText?: boolean;
}) {
    const itemColors = getCapabilityColors(item.type);
    const Icon = itemColors.icon;
    const statusStyle = getStatusStyle(item.status);
    const dateStr = item.modified ?? item.created;

    const badgeText = largeText ? 'text-[9.9px]' : 'text-[9px]';
    const bodyText = largeText ? 'text-[11px]' : 'text-[10px]';
    const titleText = largeText ? 'text-[15.4px]' : 'text-sm';
    const iconSize = largeText ? 20 : 18;
    const inlineIconSize = largeText ? 11 : 10;

    return (
        <>
            {showDetail && (
                <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5">
                    {item.latest_version && (
                        <span
                            className={`rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-500 ${badgeText}`}
                        >
                            v{item.latest_version}
                        </span>
                    )}
                    {item.status && (
                        <span
                            className={`rounded-full px-1.5 py-0.5 font-medium uppercase ${badgeText} ${statusStyle}`}
                        >
                            {item.status}
                        </span>
                    )}
                </div>
            )}

            <div
                className={
                    showDetail
                        ? 'flex flex-row items-start gap-2'
                        : 'flex h-full min-h-0 w-full flex-row items-start gap-2 overflow-hidden'
                }
            >
                <div
                    className={`flex shrink-0 items-center justify-center rounded-md p-1.5 ${itemColors.bgLight} ${itemColors.text}`}
                >
                    <Icon size={iconSize} />
                </div>
                <div
                    className={
                        showDetail
                            ? 'flex min-w-0 flex-1 flex-col'
                            : 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
                    }
                >
                    <div
                        className={
                            showDetail
                                ? 'flex items-center gap-1.5'
                                : 'flex min-w-0 items-start gap-1.5'
                        }
                    >
                        <span
                            className={`min-w-0 text-left font-bold leading-tight text-slate-800 ${titleText} ${
                                !showDetail ? 'line-clamp-2' : ''
                            }`}
                        >
                            {capabilityDisplayTitle(item)}
                        </span>
                        {showDetail && !typeFilterActive && (
                            <span
                                className={`shrink-0 rounded-full px-1.5 py-0.5 font-medium ${badgeText} ${itemColors.bgLight} ${itemColors.text}`}
                            >
                                {CAPABILITY_TYPE_LABELS[item.type] || item.type}
                            </span>
                        )}
                    </div>
                    {showDetail && (
                        <span
                            className={`text-left font-mono text-slate-500 ${bodyText}`}
                        >
                            {item.fqcn || `${item.app_key}/${item.name}`}
                        </span>
                    )}
                    {item.description && (
                        <p
                            className={`text-left leading-tight text-slate-600 ${bodyText} ${
                                showDetail ? 'mt-2' : 'mt-2 min-h-0 flex-1'
                            } ${descriptionLineClamp ? 'line-clamp-2' : ''}`}
                        >
                            {item.description}
                        </p>
                    )}
                </div>
            </div>

            {showDetail && (
                <div
                    className={`mt-auto flex items-center gap-1.5 border-t border-slate-100 pt-1.5 text-slate-500 ${bodyText}`}
                >
                    {item.tags && item.tags.length > 0 && (
                        <span className="flex items-center gap-1">
                            <Tag size={inlineIconSize} />
                            {item.tags.slice(0, 3).join(', ')}
                        </span>
                    )}
                    {typeof item.method_count === 'number' && item.method_count > 0 && (
                        <span>
                            {item.method_count} method
                            {item.method_count !== 1 ? 's' : ''}
                        </span>
                    )}
                    {item.visibility && (
                        <span className="capitalize">{item.visibility}</span>
                    )}
                    {dateStr && (
                        <span className="ml-auto flex items-center gap-1 text-slate-400">
                            <Clock size={inlineIconSize} />
                            {new Date(dateStr).toLocaleDateString()}
                        </span>
                    )}
                </div>
            )}
        </>
    );
}

export default function CapabilitiesListPage() {
    const { appKey } = useParams();
    const location = useLocation();
    const { activeFilters, setFilter, filters } = useListFilters();
    const { data, isLoading } = useCapabilityList(appKey, {
        ...activeFilters,
        per_page: 50,
    });

    const { setHeaderTitle } = useHeaderAction();
    const typeLabel =
        filters.type && CAPABILITY_TYPE_LABELS[filters.type]
            ? CAPABILITY_TYPE_LABELS[filters.type] + 's'
            : null;
    const pageTitle = typeLabel ?? (appKey ? 'Capabilities' : 'All Capabilities');
    usePageTitle(appKey ? `${pageTitle} - ${appKey}` : pageTitle);

    const titleIcon = useMemo(() => {
        const titleColors = getCapabilityColors(filters.type || 'app');
        const iconSurfaceStyle = capabilityTitleIconBackground(titleColors.hex, true);

        if (!filters.type) {
            return (
                <div
                    className={`flex h-7 w-7 items-center justify-center rounded-md ${titleColors.text}`}
                    style={iconSurfaceStyle}
                >
                    <Tag size={16} />
                </div>
            );
        }

        const Icon = titleColors.icon;
        return (
            <div
                className={`flex h-7 w-7 items-center justify-center rounded-md ${titleColors.text}`}
                style={iconSurfaceStyle}
            >
                <Icon size={16} />
            </div>
        );
    }, [filters.type]);

    const titleSubtitle = useMemo(() => {
        const scope = appKey ? `for ${appKey}` : 'across all applications';
        const target = typeLabel
            ? `${typeLabel.toLowerCase()} capability list`
            : 'capability list';
        return `Browse and manage the ${target} ${scope}.`;
    }, [appKey, typeLabel]);

    useEffect(() => {
        setHeaderTitle({
            icon: titleIcon,
            title: pageTitle,
            subtitle: titleSubtitle,
            intense: true,
        });
        return () => setHeaderTitle(null);
    }, [pageTitle, setHeaderTitle, titleIcon, titleSubtitle]);

    const items: Capability[] = data?.data ?? [];
    const meta = data?.meta;

    const allTags = useMemo(() => {
        const tagSet = new Set<string>();
        for (const item of items) {
            if (item.tags) {
                for (const t of item.tags) tagSet.add(t);
            }
        }
        return Array.from(tagSet).sort();
    }, [items]);

    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());

    useEffect(() => {
        setSelectedTags(new Set());
    }, [location.pathname, location.search]);

    const filteredItems = useMemo(() => {
        if (selectedTags.size === 0) return items;
        return items.filter(
            (item) => item.tags?.some((t) => selectedTags.has(t)),
        );
    }, [items, selectedTags]);

    const handleSort = (field: string) => {
        if (filters.sort === field) {
            setFilter('direction', filters.direction === 'asc' ? 'desc' : 'asc');
        } else {
            setFilter('sort', field);
            setFilter('direction', 'asc');
        }
    };

    const toggleTag = (tag: string) => {
        setSelectedTags((prev) => {
            const next = new Set(prev);
            if (next.has(tag)) next.delete(tag);
            else next.add(tag);
            return next;
        });
    };

    const typeFilter = !filters.type ? (
        <Select
            value={filters.type || 'all'}
            onValueChange={(v) => setFilter('type', v === 'all' ? '' : v)}
        >
            <SelectTrigger className={cn('w-44', listControlsGlassTrigger)}>
                <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {CAPABILITY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                        {CAPABILITY_TYPE_LABELS[t] || t}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    ) : undefined;

    const paginationFooter =
        meta && meta.total > 0 ? (
            <ListPaginationFooter
                meta={meta}
                onPageChange={(page) => setFilter('page', String(page))}
            />
        ) : undefined;

    return (
        <CapabilityListPanel<Capability>
            items={filteredItems}
            sortOptions={SORT_OPTIONS}
            sortField={filters.sort}
            sortDirection={filters.direction}
            onSort={handleSort}
            searchQuery={filters.search}
            onSearchChange={(v) => setFilter('search', v)}
            searchPlaceholder={`Search ${(typeLabel ?? 'capabilities').toLowerCase()} by name or description...`}
            allTags={allTags}
            selectedTags={selectedTags}
            onToggleTag={toggleTag}
            onClearAllTags={() => setSelectedTags(new Set())}
            onSelectAllTags={() => setSelectedTags(new Set(allTags))}
            isLoading={isLoading}
            emptyStateVariant="glass"
            emptyMessage={`No ${(typeLabel ?? 'capabilities').toLowerCase()} found.`}
            extraToolbar={typeFilter}
            pagination={paginationFooter}
            renderCard={(item) => {
                const itemColors = getCapabilityColors(item.type);
                const typeFilterActive = Boolean(filters.type);
                const cardBorder = `border border-slate-200 border-l-4 ${itemColors.border}`;

                return (
                    <Link
                        key={item.id}
                        to={capabilityDetailPath(
                            item.app_key,
                            item.type,
                            item.name,
                        )}
                        className="group/card relative z-0 block hover:z-40 focus-within:z-40"
                    >
                        {/* Compact card — sets grid cell size; does not grow on hover */}
                        <div
                            className={`relative z-0 flex h-[105px] min-h-[105px] w-full cursor-pointer flex-col overflow-hidden rounded-lg ${cardBorder} bg-white p-3 shadow-sm transition-shadow duration-150 group-hover/card:shadow-none group-focus-within/card:shadow-none hover:border-slate-300 hover:bg-slate-50 focus-within:border-slate-300 focus-within:bg-slate-50`}
                        >
                            <CapabilityCardInner
                                item={item}
                                typeFilterActive={typeFilterActive}
                                showDetail={false}
                                descriptionLineClamp
                            />
                        </div>

                        {/* Full detail popup — top-left aligned; +20% W/H vs compact cell */}
                        <div
                            className="pointer-events-none absolute left-0 top-0 z-10 min-h-[120%] w-[120%] min-w-0 opacity-0 transition-opacity duration-150 group-hover/card:pointer-events-auto group-hover/card:opacity-100 group-focus-within/card:pointer-events-auto group-focus-within/card:opacity-100"
                        >
                            <div
                                className="flex min-h-[105px] w-full cursor-pointer flex-col rounded-xl border-2 border-solid bg-white p-4 hover:bg-slate-50"
                                style={{
                                    borderColor: itemColors.hex,
                                    boxShadow: `0 25px 50px -12px rgb(15 23 42 / 0.2), 0 12px 36px -12px color-mix(in srgb, ${itemColors.hex} 38%, transparent)`,
                                }}
                            >
                                <CapabilityCardInner
                                    item={item}
                                    typeFilterActive={typeFilterActive}
                                    showDetail
                                    descriptionLineClamp={false}
                                    largeText
                                />
                            </div>
                        </div>
                    </Link>
                );
            }}
        />
    );
}
